import dayjs from 'dayjs';
import { cachedStripHtml } from '../../../../../../validator/utils/cachedStripHtml.ts';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IAdvancedSelectColumns } from '../IAdvancedSelectColumns.ts';
import { type IRuntimeWidgetAdvancedOption } from '../IAdvancedSelectWidget.ts';

/**
 * Altezza massima scrollabile per la tabella nella modale di selezione avanzata.
 * L'offset di `300px` tiene conto dell'intero "chrome" verticale della modale:
 * spaziatura superiore (`top: 20`), header con titolo e checkbox
 * "Mostra solo selezionate", toolbar di ricerca, eventuale footer con
 * contatore selezioni, footer con i pulsanti e padding.
 *
 * Il `max()` garantisce un'altezza minima di `260px` anche a zoom elevato
 * (ad esempio 300%+), dove il solo `calc(100vh - 300px)` potrebbe diventare
 * troppo piccolo per mantenere la tabella utilizzabile.
 */
export const ADVANCED_SELECT_TABLE_SCROLLABLE_HEIGHT =
  'max(260px, calc(100vh - 300px))';

/**
 * Funzione di sorting generica per colonne della tabella.
 */
export function sortColumnValues(
  aValue: any,
  bValue: any,
  datatype?: Datatype,
): number {
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  if (datatype === Datatype.RichText) {
    return cachedStripHtml(String(aValue)).localeCompare(
      cachedStripHtml(String(bValue)),
    );
  }

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return aValue.localeCompare(bValue);
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  }

  return String(aValue).localeCompare(String(bValue));
}

const WHITESPACE_SPLIT_REGEX = /\s+/;

/**
 * Parsa la stringa di ricerca in token attivi.
 * Tutti i token non vuoti sono attivi, indipendentemente dalla lunghezza.
 */
export function parseSearchTokens(searchValue: string): string[] {
  if (!searchValue.trim()) return [];
  return searchValue.split(WHITESPACE_SPLIT_REGEX).filter(Boolean);
}

/**
 * Verifica se un token di ricerca corrisponde al valore di una colonna, con
 * regole specifiche per tipo: Number per uguaglianza numerica, Date confrontando
 * i formati DD/MM/YYYY e YYYY-MM-DD, RichText sul testo senza tag, gli altri tipi
 * con un confronto case-insensitive di sottostringa.
 */
function matchTokenToColumnValue(
  value: any,
  token: string,
  datatype: Datatype | undefined,
): boolean {
  if (value == null || value === '') return false;

  if (datatype === Datatype.Number) {
    const numericToken = Number(token);
    if (Number.isNaN(numericToken)) return false;
    return Number(value) === numericToken;
  }

  if (datatype === Datatype.Date) {
    const recordDate = dayjs(value, 'YYYY-MM-DD', true);
    if (!recordDate.isValid()) return false;
    return (
      token === recordDate.format('DD/MM/YYYY') ||
      token === recordDate.format('YYYY-MM-DD')
    );
  }

  if (datatype === Datatype.RichText) {
    return cachedStripHtml(String(value))
      .toLowerCase()
      .includes(token.toLowerCase());
  }

  return String(value).toLowerCase().includes(token.toLowerCase());
}

/**
 * Verifica se un'opzione corrisponde ai token di ricerca globale.
 * La logica è OR tra i token: basta che uno di essi corrisponda a una colonna.
 */
export function matchRecordToSearchTokens(
  option: IRuntimeWidgetAdvancedOption,
  tokens: string[],
  columns: IAdvancedSelectColumns[],
): boolean {
  if (tokens.length === 0) return true;

  return tokens.some(token =>
    columns.some(col =>
      matchTokenToColumnValue(option.values?.[col.id], token, col.type),
    ),
  );
}
